"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  Notice,
  type Option,
  SelectField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  applyTextOptions,
  type CipherTextOptions,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  MAX_RAILS,
  MIN_RAILS,
  railCycle,
  railFenceDecode,
  railFenceEncode,
  railFenceGrid,
  railRows,
} from "@/lib/tool-kit/ciphers/rail-fence";

const GRID_LIMIT = 60;
const SPACE_MARK = "␣";
const WHITESPACE = /\s/;
const RAIL_COUNTS = Array.from(
  { length: MAX_RAILS - MIN_RAILS + 1 },
  (_, index) => MIN_RAILS + index
);
const DEFAULT_OPTIONS: CipherTextOptions = {
  keepCase: true,
  keepSpaces: false,
  keepPunctuation: false,
};

/** English strings. A translated content file overrides these through `ui`. */
export const RAIL_FENCE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "WE ARE DISCOVERED FLEE AT ONCE",
  rails: "Number of rails",
  offset: "Offset",
  offsetHint:
    "Positions of the zigzag to skip before the first character. 0 starts on the top rail.",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  keepHint:
    "Whatever you keep travels along the fence like a letter; anything else is removed before the message is written out.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  bruteForce: "Try every rail count (2–10)",
  bruteHeading: "Every rail count at offset {offset}",
  railCount: "one: {count} rail|other: {count} rails",
  working: "The zigzag",
  gridLabel: "Rail fence grid",
  railLabel: "Rail {n}",
  gridIntro:
    "The plain text runs down and up across the rails. Reading each rail from left to right, top rail first, gives the cipher text.",
  gridTruncated:
    "one: Showing the first character.|other: Showing the first {count} characters.",
  readRails: "Reading the rails",
  emptyGrid: "Type a message to see the zigzag.",
} as const;

function visible(char: string | null): string | null {
  if (char === null) {
    return null;
  }
  return WHITESPACE.test(char) ? SPACE_MARK : char;
}

export default function RailFenceCipherWidget() {
  const strings = useToolUi(RAIL_FENCE_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [rails, setRails] = useState(3);
  const [offset, setOffset] = useState(0);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [bruteForce, setBruteForce] = useState(false);

  const prepared = applyTextOptions(input, options);
  const output =
    mode === "encode"
      ? railFenceEncode(prepared, rails, offset)
      : railFenceDecode(prepared, rails, offset);
  const plain = [...(mode === "encode" ? prepared : output)];
  const shown = plain.slice(0, GRID_LIMIT).join("");
  const grid = railFenceGrid(shown, rails, offset);
  const rows = railRows(plain.join(""), rails, offset);

  const railOptions: Option[] = RAIL_COUNTS.map((count) => ({
    value: String(count),
    label: fmt(count),
  }));
  const offsetOptions: Option[] = Array.from(
    { length: railCycle(rails) },
    (_, index) => ({ value: String(index), label: fmt(index) })
  );

  function changeRails(value: string) {
    const next = Number(value);
    setRails(next);
    setOffset((current) => current % railCycle(next));
  }

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="rf-rails"
          label={strings.rails}
          onChange={changeRails}
          options={railOptions}
          value={String(rails)}
        />
        <SelectField
          hint={strings.offsetHint}
          id="rf-offset"
          label={strings.offset}
          onChange={(value) => setOffset(Number(value))}
          options={offsetOptions}
          value={String(offset)}
        />
      </div>
      {mode === "decode" ? (
        <Checkbox
          checked={bruteForce}
          label={strings.bruteForce}
          onChange={setBruteForce}
        />
      ) : null}
    </div>
  );

  const brute =
    mode === "decode" && bruteForce && prepared.length > 0 ? (
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>
          {t(strings.bruteHeading, { offset: fmt(offset) })}
        </h2>
        <ul className="divide-y divide-border">
          {RAIL_COUNTS.map((count) => (
            <li className="flex gap-3 py-2" key={count}>
              <span className="w-20 shrink-0 text-muted-foreground text-xs">
                {tPlural(strings.railCount, locale, count, {
                  count: fmt(count),
                })}
              </span>
              <span className="break-all font-mono text-foreground text-sm">
                {railFenceDecode(prepared, count, offset % railCycle(count))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const working =
    plain.length > 0 ? (
      <>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.gridIntro}
        </p>
        <CipherGrid
          cells={grid.flat().map(visible)}
          columns={[...shown].length}
          dense
          label={strings.gridLabel}
          rowHeaders={grid.map((_, index) =>
            t(strings.railLabel, { n: fmt(index + 1) })
          )}
        />
        {plain.length > GRID_LIMIT ? (
          <p className={HINT}>
            {tPlural(strings.gridTruncated, locale, GRID_LIMIT, {
              count: fmt(GRID_LIMIT),
            })}
          </p>
        ) : null}
        <div className="space-y-2">
          <p className={LABEL}>{strings.readRails}</p>
          <ol className="space-y-1">
            {rows.map((row, index) => (
              <li
                className="flex gap-3 text-sm"
                // biome-ignore lint/suspicious/noArrayIndexKey: one row per rail
                key={index}
              >
                <span className="w-16 shrink-0 text-muted-foreground">
                  {t(strings.railLabel, { n: fmt(index + 1) })}
                </span>
                <span className="break-all font-mono text-foreground">
                  {[...row].map((char) => visible(char)).join("")}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </>
    ) : (
      <Notice>{strings.emptyGrid}</Notice>
    );

  return (
    <CipherWorkbench
      extras={brute}
      idPrefix="rf"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={<p className={HINT}>{strings.keepHint}</p>}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={working}
    />
  );
}
